import * as Sentry from '@sentry/vue';

// Acquisition user properties, the names must stay as they are for the Amplitude -> Google Ads conversion forwarding.
// Keep these lists in sync with voicemod.net (voicemod-page-view.js)
const CAMPAIGN_PROPERTIES = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
] as const;
const CLICK_ID_PROPERTIES = ['gclid', 'fbclid'] as const;
const ACQUISITION_PROPERTIES = [...CAMPAIGN_PROPERTIES, ...CLICK_ID_PROPERTIES];

// utm_source values our own links and apps add, they aren't an acquisition channel
const INTERNAL_UTM_SOURCES = [
	'voicemod',
	'vm web menu',
	'vm desktop',
	'flagship',
	'v2',
	'tuna',
];

// Set by voicemod.net on .voicemod.net: base64 of the full URL of the visitor's first visit
const LANDING_URL_COOKIE = 'vm_initial_url';
const SAVED_VALUES_KEY = 'acquisition-values';

type AcquisitionProperty = (typeof ACQUISITION_PROPERTIES)[number];
export type AcquisitionValues = Partial<Record<AcquisitionProperty, string>>;

// Trimmed value, or null when it's missing or blank (e.g. `?gclid=%20`)
const getFilledValue = (value: string | null): string | null =>
	value !== null && value.trim() !== '' ? value.trim() : null;

// Campaign values from our own links describe the click, not how the user was acquired, so they're dropped. Click IDs
// are kept, only ad platforms add them
const getValuesFromParams = (params: URLSearchParams): AcquisitionValues => {
	const utmSource = getFilledValue(params.get('utm_source'));
	const isInternalCampaign =
		utmSource !== null &&
		INTERNAL_UTM_SOURCES.includes(utmSource.toLowerCase());
	const properties: readonly AcquisitionProperty[] = isInternalCampaign
		? CLICK_ID_PROPERTIES
		: ACQUISITION_PROPERTIES;

	const values: AcquisitionValues = {};
	properties.forEach((property) => {
		const value = getFilledValue(params.get(property));
		if (value !== null) {
			values[property] = value;
		}
	});

	return values;
};

// Each property is taken from the first values that have it
const mergeValues = (...valuesList: AcquisitionValues[]): AcquisitionValues =>
	valuesList.reduce((merged, values) => ({ ...values, ...merged }), {});

// With hash history the query can be before the hash (?utm_source=...#/) or in it (#/?utm_source=...)
const getAccountsUrlValues = (): AcquisitionValues => {
	const { search, hash } = window.location;
	const hashQueryStart = hash.indexOf('?');
	const hashQuery =
		hashQueryStart === -1 ? '' : hash.substring(hashQueryStart + 1);

	return mergeValues(
		getValuesFromParams(new URLSearchParams(search)),
		getValuesFromParams(new URLSearchParams(hashQuery))
	);
};

const getLandingCookieValues = (): AcquisitionValues => {
	const cookieEntry = document.cookie
		.split('; ')
		.find((entry) => entry.startsWith(`${LANDING_URL_COOKIE}=`));

	if (!cookieEntry) {
		return {};
	}

	const rawValue = cookieEntry.substring(cookieEntry.indexOf('=') + 1);

	try {
		const landingUrl = new URL(atob(decodeURIComponent(rawValue)));

		return getValuesFromParams(landingUrl.searchParams);
	} catch (error) {
		Sentry.addBreadcrumb({
			type: 'amplitude',
			message: `Failed to parse ${LANDING_URL_COOKIE} cookie: ${
				(error as Error).message
			}`,
			level: 'warning',
		});

		return {};
	}
};

const getSavedValues = (): AcquisitionValues => {
	try {
		return getValuesFromParams(
			new URLSearchParams(sessionStorage.getItem(SAVED_VALUES_KEY) ?? '')
		);
	} catch {
		return {};
	}
};

/**
 * Saves the Accounts URL values of the tab's first page load. Redirects drop the query before the sign-up completes
 * (social logins leave Accounts, /authorize redirects to the login without it), so it has to be read on load.
 */
export const saveAcquisitionValues = (): void => {
	try {
		if (sessionStorage.getItem(SAVED_VALUES_KEY) !== null) {
			return;
		}

		const values = getAccountsUrlValues();
		if (Object.keys(values).length > 0) {
			sessionStorage.setItem(
				SAVED_VALUES_KEY,
				new URLSearchParams(values).toString()
			);
		}
	} catch {
		// The browser can block the storage, sign-ups still read the current URL and the landing cookie
	}
};

export const clearAcquisitionValues = (): void => {
	try {
		sessionStorage.removeItem(SAVED_VALUES_KEY);
	} catch {
		// Nothing was saved if the storage is blocked
	}
};

/**
 * The values that brought the user in: the Accounts URL first (as saved on the first page load, then the current one),
 * then the voicemod.net landing URL. Each property comes from the first source that has it.
 */
export const getAcquisitionValues = (): AcquisitionValues =>
	mergeValues(
		getSavedValues(),
		getAccountsUrlValues(),
		getLandingCookieValues()
	);
